import { Collapsible } from "@radix-ui/react-collapsible";
import { NodeViewWrapper } from "@tiptap/react";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { useContentAreaSize } from "../../../size-context";
import { useIsNestedSnippet, useIsRecursiveSnippet } from "../nested-snippet-context";
import { getSnippetSizeClasses } from "../utils";

import useSnippetContent from "./hooks/useSnippetContent";
import SnippetView from "./SnippetView";

interface SnippetDisplayProps {
  promptName: string;
  version?: string;
  label?: string;
  isReadOnly?: boolean;
  className?: string;
  onEdit?: () => void;
  onVersionLabelChange?: (version: number | undefined, label: string | undefined) => void;
}

const SnippetDisplay = observer(
  ({
    promptName,
    version,
    label,
    isReadOnly,
    className,
    onEdit,
    onVersionLabelChange,
  }: SnippetDisplayProps) => {
    const isNested = useIsNestedSnippet();
    const isRecursive = useIsRecursiveSnippet(promptName);

    const [content, isLoading, sourcedPrompt, format, href, promptId] = useSnippetContent(
      promptName,
      Number(version),
      label,
    );

    const [isCollapsed, setIsCollapsed] = useState(isNested || isReadOnly);
    const size = useContentAreaSize();

    const shouldShowCollapsed = isCollapsed;
    const toggleCollapsed = () => setIsCollapsed(!isCollapsed);

    if (isRecursive) {
      return (
        <NodeViewWrapper className="w-full min-w-0">
          <div
            className={`flex w-full min-w-0 items-center gap-2 rounded border border-dashed border-red-300 bg-red-50/50 px-3 py-2 ${getSnippetSizeClasses(
              size,
              "text",
            )} ${className || ""}`}
          >
            <span className="font-mono text-red-600">{promptName}</span>
            <span className="text-red-500">(recursive reference)</span>
          </div>
        </NodeViewWrapper>
      );
    }

    return (
      <NodeViewWrapper className="w-full min-w-0">
        <Collapsible
          open={!shouldShowCollapsed}
          onOpenChange={open => setIsCollapsed(!open)}
          className="w-full min-w-0"
        >
          <div
            className={`group/Snippet flex w-full min-w-0 flex-col rounded bg-blue-50/30 transition-all duration-150 ease-in-out hover:border-blue-500 hover:bg-blue-50/50 focus:outline-none  active:bg-blue-50/60 ${getSnippetSizeClasses(
              size,
              "text",
            )} ${className || ""}`}
          >
            <SnippetView
              promptName={promptName}
              sourcedPrompt={sourcedPrompt}
              version={version ? Number(version) : undefined}
              label={label}
              prompt={content}
              onEdit={onEdit}
              format={format}
              isCollapsed={shouldShowCollapsed}
              toggleCollapsed={toggleCollapsed}
              isLoading={isLoading}
              href={href}
              isReadOnly={isReadOnly}
              promptId={promptId}
              onVersionLabelChange={onVersionLabelChange}
            />
          </div>
        </Collapsible>
      </NodeViewWrapper>
    );
  },
);

export default SnippetDisplay;
